import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";

const WebDesignAndDevelopment = () => {
  const phrases = [
    "Web Design  ",
    "Web Development"
  ];

  const cardData = [
    { type: 'purple-clay', content: '🔮' },
    { type: 'smooth-wave', content: '🧬' },
    { type: 'abstract-blocks', content: '📊' },
    { type: 'pink-sphere', content: '⚪' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const designServices = [
    { icon: "🎨", title: "Custom Website Design", desc: "Unique and professional website designs tailored to your business goals and brand identity." },
    { icon: "📱", title: "Responsive Web Design", desc: "Fully responsive websites that provide a seamless experience across desktops, tablets, and smartphones." },
    { icon: "🖥️", title: "UI/UX Design", desc: "User-centric interfaces designed to improve navigation, engagement, and customer satisfaction." },
    { icon: "🌈", title: "Landing Page Design", desc: "High-converting landing pages designed for marketing campaigns, lead generation, and product promotions." },
    { icon: "🛍️", title: "E-Commerce Design", desc: "Modern online store designs with intuitive navigation, attractive product pages, and smooth checkout experiences." },
    { icon: "🔄", title: "Website Redesign", desc: "Transform outdated websites into modern, visually appealing, and performance-driven digital platforms." },
    { icon: "🌟", title: "Corporate Website Design", desc: "Professional websites that represent your company with elegance, credibility, and functionality." }
  ];

  const devServices = [
    { icon: "⚛️", title: "Custom Website Development", desc: "Develop fully customized websites tailored to your business requirements and future growth." },
    { icon: "🚀", title: "Full Stack Development", desc: "End-to-end web application development using modern technologies including React, Node.js, Express.js, MongoDB, PHP, and Laravel." },
    { icon: "🛒", title: "E-Commerce Development", desc: "Develop secure online stores with shopping carts, payment gateway integration, inventory management, and customer dashboards." },
    { icon: "📊", title: "CMS Development", desc: "Build content management systems using WordPress and custom CMS platforms that allow easy content updates." },
    { icon: "🔗", title: "API Development & Integration", desc: "Integrate third-party services including payment gateways, CRM systems, ERP solutions, social media APIs, and cloud platforms." },
    { icon: "☁️", title: "Cloud-Based Web Applications", desc: "Develop scalable cloud-enabled applications with secure architecture and reliable performance." },
    { icon: "🔐", title: "Secure Web Development", desc: "Implement industry-standard security measures including SSL, authentication, data encryption, firewall protection, and secure coding practices." },
    { icon: "⚡", title: "Website Performance Optimization", desc: "Improve website speed through code optimization, image compression, caching, CDN integration, and database optimization." },
    { icon: "🔄", title: "Website Maintenance & Support", desc: "Continuous monitoring, bug fixes, feature enhancements, backups, security updates, and technical support." }
  ];

  const technologies = {
    "Frontend": ["HTML5", "CSS3", "JavaScript (ES6+)", "React JS", "Bootstrap", "Tailwind CSS", "Material UI"],
    "Backend": ["Node.js", "Express.js", "PHP", "Laravel"],
    "Database": ["MongoDB", "MySQL"],
    "CMS": ["WordPress", "Custom CMS"],
    "Tools & Cloud": ["Git & GitHub", "REST APIs", "AWS", "Firebase", "Docker"]
  };

  const processSteps = [
    { num: "01", title: "Discovery & Consultation", desc: "Understanding your business goals, target audience, and project requirements." },
    { num: "02", title: "Planning & Strategy", desc: "Creating wireframes, sitemaps, project architecture, and technology selection." },
    { num: "03", title: "UI/UX Design", desc: "Designing attractive, user-friendly interfaces that enhance engagement." },
    { num: "04", title: "Development", desc: "Building responsive, scalable, and secure websites using modern technologies." },
    { num: "05", title: "Testing & QA", desc: "Comprehensive testing for performance, security, compatibility, and responsiveness." },
    { num: "06", title: "Deployment", desc: "Launching your website with optimized performance and secure hosting configuration." },
    { num: "07", title: "Ongoing Support", desc: "Providing continuous maintenance, updates, monitoring, and technical assistance." }
  ];

  const industries = [
    "Information Technology", "Healthcare", "Education", "Real Estate", "Finance & Banking",
    "Manufacturing", "Retail & E-Commerce", "Travel & Tourism", "Hospitality", "Logistics",
    "Startups", "Small & Medium Businesses", "Corporate Enterprises"
  ];

  const designBenefits = [
    "Modern & Creative Layouts", "Mobile-First Design", "User-Friendly Navigation",
    "Fast Loading Pages", "Brand-Focused Visuals", "SEO-Friendly Structure",
    "Conversion-Oriented Design", "Cross-Browser Compatibility"
  ];

  const whyChooseUs = [
    "Experienced Web Designers & Developers", "Custom Business Solutions", "Modern UI/UX Design",
    "SEO-Friendly Development", "Mobile Responsive Websites", "Secure & Scalable Architecture",
    "Fast Website Performance", "Affordable Pricing", "On-Time Project Delivery",
    "Dedicated Technical Support", "Latest Technology Stack", "100% Client Satisfaction Focus"
  ];

  return (
    <>



      <Helmet>
        {/* Primary SEO */}
        <title>
          Web Design & Web Development Services | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Web Design & Web Development Services | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. offers professional web design and web development services, including responsive website design, custom web applications, React.js development, Node.js development, eCommerce solutions, UI/UX design, CMS development, API integration, and SEO-friendly websites that drive business growth."
        />

        <meta
          name="keywords"
          content="Web Design, Web Development, Website Design Company, Website Development Company, Responsive Web Design, Custom Website Development, React JS Development, Node JS Development, MERN Stack Development, Full Stack Development, UI UX Design, Frontend Development, Backend Development, E-Commerce Website Development, WordPress Development, CMS Development, API Integration, Website Maintenance, SEO Friendly Website, Business Website Development, Corporate Website Design, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/web-design-and-development"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Web Design & Web Development Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Build high-performance, responsive, and SEO-optimized websites with Affsoft IT Solutions Pvt. Ltd. We specialize in custom web development, React.js, Node.js, eCommerce solutions, UI/UX design, and scalable business applications."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/web-design-and-development"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Web Design & Web Development Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Create modern, responsive, and SEO-friendly websites with Affsoft IT Solutions Pvt. Ltd. Our expert team delivers custom web design, web development, React.js, Node.js, and full-stack solutions for businesses of all sizes."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="web-design-dev-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a0915' // dark fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.15 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>

          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Web Design  &nbsp; and &nbsp;</span>
              <span>Web Development </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-warning fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Web Design and Web Development</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we create modern, responsive, and high-performing websites that help businesses establish a strong online presence. Our web design and development services combine creativity, technology, and user experience to deliver websites that not only look exceptional but also generate leads and drive business growth.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>About Affsoft</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-warning fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= WEB DESIGN SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-primary px-3 py-2 rounded-pill mb-2 text-uppercase">Web Design</span>
              <h2 className="fw-bold text-dark display-6">Creative Designs That Leave a Lasting Impression</h2>
              <p className="text-muted mt-3">
                Your website is often the first interaction customers have with your business. At Affsoft IT Solutions Pvt. Ltd., we create attractive, intuitive, and user-focused website designs that build trust and encourage visitors to take action.
              </p>
            </div>

            <div className="row g-4">
              {designServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-primary">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Web Design Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Why Choose Our Web Design Services?</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= WEB DEVELOPMENT SECTION ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-success px-3 py-2 rounded-pill mb-2 text-uppercase">Web Development</span>
              <h2 className="fw-bold text-dark display-6">Powerful, Secure & Scalable Solutions</h2>
              <p className="text-muted mt-3">
                At Affsoft IT Solutions Pvt. Ltd., we develop reliable, secure, and feature-rich websites using the latest technologies and development standards. Whether you need a simple business website, an enterprise portal, or a custom web application, our development team delivers scalable solutions that grow with your business.
              </p>
            </div>

            <div className="row g-4">
              {devServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-success">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-warning text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Tech Stack</span>
              <h2 className="fw-bold display-6">Technologies We Leverage</h2>
              <p className="text-light-50">We build future-proof websites using modern industry-standard software ecosystems.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-warning mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-warning opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= DEVELOPMENT PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Roadmap</span>
              <h2 className="fw-bold text-dark display-6">Our Agile Development Process</h2>
              <p className="text-muted">A streamlined step-by-step methodology to transform ideas into digital reality.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-primary border-4 h-100 shadow-sm">
                    <div className="fw-bold text-primary display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Industries We Serve</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Choose Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We provide innovative, robust, and client-centric digital services built to scale.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-primary fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default WebDesignAndDevelopment;